import React, { useState, useEffect, useRef } from "react";
import focusSource from "ally.js/amd/style/focus-source";
import "./App.scss";
import { I18nContext } from "./context";
import { calculateIncome, INCOME_PARAMS } from "./lib/calculator";
import { fetchDataFromUrl } from "./lib/helpers";
import Header from "./components/Header/Header";
import IncomeConfigurator from "./components/IncomeConfigurator/IncomeConfigurator";
import RegionConfigurator from "./components/RegionConfigurator/RegionConfigurator";
import Navigation from "./components/Navigation/Navigation";
import Result from "./components/Result/Result";
import Sharing from "./components/Sharing/Sharing";
import Info from "./components/Info/Info";
import Dialog from "./components/Dialog/Dialog";
import Next from "./components/Next/Next";

focusSource();

function App({ api, lang, defaultLocale, sharingUrl, platformUrl, prefilled }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [locale, setLocale] = useState({});
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState({});

  const [income, setIncome] = useState({});
  const [result, setResult] = useState({});

  const [regionDialog, setRegionDialog] = useState(null);

  const [isConfigured, setIsConfigured] = useState(false);

  const configurationSection = useRef(null);
  const resultsSection = useRef(null);
  const sharingSection = useRef(null);
  const infoSection = useRef(null);

  const scrollTo = (section) => {
    if (section.current) {
      section.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const showIncomeConfiguration = () => {
    scrollTo(configurationSection);
  };
  const showRegionConfiguration = () => {
    if (regionDialog) {
      regionDialog.show();
    }
  };
  const setRegionAndShowResults = () => {
    if (regionDialog) {
      regionDialog.hide();
    }

    setImmediate(() => {
      scrollTo(resultsSection);
    });
  };
  const setIncomeAndShowResults = (income) => {
    setIncome(income);
    setIsConfigured(true);

    setImmediate(() => {
      scrollTo(resultsSection);
    });
  };

  // Initial data
  useEffect(() => {
    setLocale(defaultLocale);

    const fetchData = async () => {
      setIsError(false);
      setIsLoading(true);

      try {
        const [locale, regions] = await Promise.all([
          fetchDataFromUrl(api.locale, { lang }),
          fetchDataFromUrl(api.regions, { lang }),
        ]);

        setLocale({ ...defaultLocale, ...locale });
        setRegions(regions);

        // Set default region
        setRegion(regions[0]);
      } catch (err) {
        console.log(err);

        setIsError(true);
      }

      setIsLoading(false);

      // Prefill income if passed via URL, e.g.
      if (prefilled) {
        const prefilledIncome = {};

        INCOME_PARAMS.forEach((param, i) => {
          prefilledIncome[param] = prefilled[i] || 0;
        });

        setIncome(prefilledIncome);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Change income or region
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const fetchData = async () => {
      try {
        let newResult = await fetchDataFromUrl(api.income, {
          lang,
          income: calculateIncome(income),
          region: region.Code,
        });

        newResult.isTop = newResult.richestPerson < 1;

        setResult(newResult);
      } catch (err) {
        console.log(err);

        setIsError(true);
      }
    };

    fetchData();
  }, [income, region]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render
  return (
    <I18nContext.Provider value={{ locale, lang }}>
      <div className={`App${isConfigured ? ` App--configured` : ""}`} id="main">
        {isLoading ? (
          <div className="App__message App__message--loading">
            <p>{locale.loadingMessage}</p>
          </div>
        ) : isError ? (
          <div
            className="App__message App__message--_error"
            dangerouslySetInnerHTML={{
              __html: ` <p>${locale.errorMessage}</p>`,
            }}
          />
        ) : (
          <>
            <div className="App__section" ref={configurationSection}>
              <Header {...{ logoUrl: platformUrl }} />
              <IncomeConfigurator
                {...{ income, setIncome: setIncomeAndShowResults }}
              />
            </div>
            <div className="App__section" ref={resultsSection}>
              <Navigation
                {...{
                  income,
                  region,
                  showIncomeConfiguration,
                  showRegionConfiguration,
                }}
              />
              <Dialog
                id="region-dialog"
                appRoot="#main"
                dialogRoot="#dialog-root"
                dialogRef={(dialog) => setRegionDialog(dialog)}
                title={locale.regionTitle}
                closeButtonLabel={locale.regionCloseLabel}
                closeButtonContent=""
                classNames={{
                  element: "Dialog",
                  title: "Dialog__title",
                  document: "Dialog__content",
                  closeButton: "Dialog__close",
                }}
              >
                <RegionConfigurator
                  {...{
                    regions,
                    setRegion,
                    onSetRegion: setRegionAndShowResults,
                  }}
                />
              </Dialog>
              <Result
                {...{
                  income,
                  region,
                  result,
                  showNextSection: () => scrollTo(sharingSection),
                }}
              />
            </div>
            <div className="App__section" ref={sharingSection}>
              <Sharing {...{ url: sharingUrl, income, region, result }} />
              <Next onClick={() => scrollTo(infoSection)} />
            </div>
            <div className="App__section" ref={infoSection}>
              <Info url={platformUrl} />
            </div>
          </>
        )}
      </div>
      <div id="dialog-root"></div>
    </I18nContext.Provider>
  );
}

export default App;
